import React, { useEffect, useState } from "react";
import Breadcrums from "../../components/breadcrums";
import { lcaStatementBreadcrums } from "../../constants/breadcrums";
import { sectionClass, payrollCommandActions } from "../../constants/common";
import PageTitle from "../../components/pageTitle";
import { listOfEmployees } from "../../api/consultants-api";
import classNames from "classnames";
import CommonFilters from "../../blocks/commonFilters";
import { getLCAStatementData } from "../../api/lca-statement-api";
import { ColumnDef } from "@tanstack/react-table";
import TanstackTable from "../../components/TanstackTable";
import Currency from "react-currency-formatter";
import logger from "../../utils/logger";

interface LCAStatementRow {
    uuid: string;
    name: string;
    month: string;
    month1: string;
    year: number;
    totalHrs: number;
    actual_wages: number;
    total_earnings: number;
    location_1: string;
    location_2: string;
    location_3: string;
    lca_rate_1: number;
    lca_rate_2: number;
    lca_rate_3: number;
    calculated_wages_1: number;
    calculated_wages_2: number;
    calculated_wages_3: number;
}

export default function LCAStatement() {
    const [isLoading, setLoading] = React.useState(false);
    const [people, setPeople] = useState<any[]>([]);
    const [data, setData] = useState<LCAStatementRow[]>([]);
    const [showWithLCARatesOnly, setShowWithLCARatesOnly] = useState(false);

    const columns = React.useMemo<ColumnDef<LCAStatementRow, any>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Employee",
                cell: (info) => info.getValue(),
                enableColumnFilter: false,
                enableGrouping: true,
                footer: ({ table }) => (
                    <span className="font-semibold text-md">Total</span>
                ),
            },
            {
                accessorKey: "month1",
                header: "Month",
                cell: (info) => `${info.getValue()} ${info.row.original.year}`,
                enableColumnFilter: false,
                enableGrouping: false,
                meta: {},
            },
            {
                accessorKey: "totalHrs",
                header: "Worked Hours",
                cell: (info) => (Number(info.getValue()) || 0).toFixed(2),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-md font-semibold">
                        {table
                            .getFilteredRowModel()
                            .rows.reduce((total, row) => total + (row.original.totalHrs || 0), 0)
                            .toFixed(2)}
                    </span>
                ),
            },
            {
                accessorKey: "actual_wages",
                header: "Actual Wages Paid",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-md font-semibold">
                        <Currency 
                            quantity={table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.actual_wages || 0), 0)} 
                            currency="USD" 
                        />
                    </span>
                ),
            },
            {
                accessorKey: "lca_rate_1",
                header: "LCA Rate 1",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-xs text-gray-500">-</span>
                ),
            },
            {
                accessorKey: "lca_rate_2",
                header: "LCA Rate 2",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-xs text-gray-500">-</span>
                ),
            },
            {
                accessorKey: "lca_rate_3",
                header: "LCA Rate 3",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-xs text-gray-500">-</span>
                ),
            },
            {
                accessorKey: "calculated_wages_1",
                header: "Calculated Wages 1",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-md font-semibold">
                        <Currency 
                            quantity={table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.calculated_wages_1 || 0), 0)} 
                            currency="USD" 
                        />
                    </span>
                ),
            },
            {
                accessorKey: "calculated_wages_2",
                header: "Calculated Wages 2",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-md font-semibold">
                        <Currency 
                            quantity={table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.calculated_wages_2 || 0), 0)} 
                            currency="USD" 
                        />
                    </span>
                ),
            },
            {
                accessorKey: "calculated_wages_3",
                header: "Calculated Wages 3",
                cell: (info) => (
                    <Currency quantity={info.getValue() || 0} currency="USD" />
                ),
                enableGrouping: false,
                enableColumnFilter: false,
                meta: {
                    cellClassName: "text-right",
                },
                footer: ({ table }) => (
                    <span className="text-md font-semibold">
                        <Currency 
                            quantity={table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original.calculated_wages_3 || 0), 0)} 
                            currency="USD" 
                        />
                    </span>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        setLoading(true);
        listOfEmployees()
            .then((data) => setPeople(data))
            .finally(() => setLoading(false));
    }, []);

    async function applyFilters(filters: any): Promise<void> {
        setLoading(true);
        setData([]);

        try {
            const result = await getLCAStatementData(filters);
            
            if (result && result.length > 0) {
                const enrichedData: LCAStatementRow[] = result.map((row: any) => ({
                    ...row,
                }));

                console.log("Enriched Data:", JSON.stringify(enrichedData, null, 2));
                setData(enrichedData);
            } else {
                setData([]);
            }
        } catch (error) {
            logger.error("Error applying filters:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    }

    // Filter data based on LCA rates filter
    const filteredData = React.useMemo(() => {
        if (!showWithLCARatesOnly) return data;
        
        return data.filter((row) => {
            const rate1 = Number(row.lca_rate_1);
            const rate2 = Number(row.lca_rate_2);
            const rate3 = Number(row.lca_rate_3);
            
            const isValidRate = (rate: number) => !isNaN(rate) && rate > 0;
            
            return isValidRate(rate1) || isValidRate(rate2) || isValidRate(rate3);
        });
    }, [data, showWithLCARatesOnly]);

    return (
        <div>
            <main>
                <Breadcrums 
                    breadcrumLinks={lcaStatementBreadcrums} 
                    commands={payrollCommandActions} 
                    module={"LCA Statement"} 
                    groups={["Statement"]} 
                />
                <PageTitle 
                    title={"Resource-wise LCA Statement"} 
                    preSummary={"Organizational Overview"} 
                    summary={"Comparison of actual wages paid vs calculated wages based on LCA hourly rate."} 
                />
                <div className={classNames(sectionClass)}>
                    <CommonFilters people={people} applyFilters={applyFilters} clientId={null} />
                    
                    <div className="mt-4 px-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={showWithLCARatesOnly}
                                onChange={() => setShowWithLCARatesOnly(!showWithLCARatesOnly)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700">Show only records with LCA rates (1, 2, or 3)</span>
                        </label>
                    </div>
                    
                    {filteredData && filteredData.length > 0 && (
                        <TanstackTable columns={columns} data={filteredData} downloadBtn={true} />
                    )}
                    {isLoading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="text-gray-500">Loading data...</div>
                        </div>
                    )}
                    {!isLoading && filteredData.length === 0 && (
                        <div className="flex items-center justify-center py-8">
                            <div className="text-gray-500">No data available. Apply filters to view report.</div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
